import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DocDocument, DocTemplate, DocStatus, BrandConfig } from '../../../types/docops';
import { saveDocument } from '../../../services/firestore/docopsService';
import { C, STATUS_CONFIG, ALL_STATUSES, GEN_MESSAGES } from './docopsConstants';
import { ReviewPanel } from './ReviewPanel';
import { PlaceholderModal } from './PlaceholderModal';
import { useAttorneyReview } from '../../../hooks/useAttorneyReview';
import { useLayout } from '../../../contexts/LayoutContext';

interface DocumentViewProps {
  documents: DocDocument[];
  templates: DocTemplate[];
  selectedDocId: string | null;
  activeBrandId: string | null;
  activeBrandConfig: BrandConfig | null;

  // Status saving state (from parent orchestrator)
  saving: boolean;

  // Generation state
  generating: boolean;
  genError: string | null;
  genResult: { elapsed: number; score?: number; passed?: boolean; attempts?: number; hasGate: boolean } | null;
  genMsgIdx: number;
  auditProgress: { attempt: number; maxAttempts: number; phase: 'generating' | 'scoring' | 'fixing'; score?: number; passed?: boolean } | null;

  // Callbacks
  goBack: () => void;
  generateDocument: (docId: string) => void;
  updateDocStatus: (docId: string, newStatus: DocStatus) => void;
  onDocumentUpdated: (doc: DocDocument) => void;
  clearGenError: () => void;
  clearGenResult: () => void;
}

export const DocumentView: React.FC<DocumentViewProps> = ({
  documents,
  templates,
  selectedDocId,
  activeBrandId,
  activeBrandConfig,
  saving,
  generating,
  genError,
  genResult,
  genMsgIdx,
  auditProgress,
  goBack,
  generateDocument,
  updateDocStatus,
  onDocumentUpdated,
  clearGenError,
  clearGenResult,
}) => {
  // ── INTERNAL EDITING STATE ──
  const [editingContent, setEditingContent] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTab, setEditTab] = useState<'source' | 'preview'>('source');
  const [contentSaving, setContentSaving] = useState(false);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);

  // ── Layout context for immersive review mode ──
  const { setHideSidebar } = useLayout();

  // ── Thomas / GC Review ──
  const {
    session: reviewSession,
    isActive: reviewActive,
    isLoading: reviewLoading,
    isGenerating: reviewGenerating,
    error: reviewError,
    startReview: rawStartReview,
    restartReview: rawRestartReview,
    sendMessage: reviewSendMessage,
    skipIssue: reviewSkipIssue,
    endReview: rawEndReview,
    generateFinal: reviewGenerateFinal,
  } = useAttorneyReview(activeBrandId, onDocumentUpdated);

  // Wrap startReview, restartReview, and endReview to toggle immersive mode
  const startReview = useCallback(
    (doc: DocDocument, tpl: DocTemplate, config: BrandConfig) => {
      setHideSidebar(true);
      rawStartReview(doc, tpl, config);
    },
    [rawStartReview, setHideSidebar],
  );

  const restartReview = useCallback(
    (doc: DocDocument, tpl: DocTemplate, config: BrandConfig) => {
      setHideSidebar(true);
      rawRestartReview(doc, tpl, config);
    },
    [rawRestartReview, setHideSidebar],
  );

  const reviewEndReview = useCallback(() => {
    setHideSidebar(false);
    rawEndReview();
  }, [rawEndReview, setHideSidebar]);

  // Also restore sidebar if user clicks Back while review is active
  const handleGoBack = useCallback(() => {
    if (reviewActive) setHideSidebar(false);
    goBack();
  }, [reviewActive, setHideSidebar, goBack]);

  // ── Section highlighting (scroll to referenced section) ──
  const docContentRef = useRef<HTMLDivElement>(null);
  const lastHighlight = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!reviewSession || !docContentRef.current) return;
    const msgs = reviewSession.conversationHistory;
    if (msgs.length === 0) return;
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg.role !== 'thomas') return;

    // Find section references in Thomas's latest message
    const sectionRe = /(?:Section|§|ARTICLE|Part)\s*(\d+(?:\.\d+)?)/gi;
    const matches = [...lastMsg.content.matchAll(sectionRe)];
    if (matches.length === 0) return;

    const sectionNum = matches[0][1];
    const container = docContentRef.current;

    // Search headings and bold text for matching section numbers
    const candidates = container.querySelectorAll('h1, h2, h3, h4, strong');
    for (const el of candidates) {
      if (el.textContent?.includes(sectionNum)) {
        // Remove previous highlight
        if (lastHighlight.current) {
          lastHighlight.current.classList.remove('dc-section-highlight');
        }
        (el as HTMLElement).classList.add('dc-section-highlight');
        lastHighlight.current = el as HTMLElement;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Auto-remove after 5s
        setTimeout(() => {
          (el as HTMLElement).classList.remove('dc-section-highlight');
        }, 5000);
        break;
      }
    }
  }, [reviewSession?.conversationHistory.length]);

  // ── HELPERS ──

  const getDepInfo = (doc: DocDocument) => {
    const tpl = templates.find(t => t.id === doc.templateId);
    const blockedByIds = tpl?.dependencies ?? [];
    const blockedByNames = blockedByIds.map(id => {
      const t = templates.find(x => x.id === id);
      return t?.title ?? id;
    });
    const blocksTemplates = templates.filter(t => t.dependencies?.includes(doc.templateId));
    const blocksNames = blocksTemplates.map(t => t.title);
    return {
      blocks: blocksTemplates.length,
      blockedBy: blockedByIds.length,
      blocksNames,
      blockedByNames,
    };
  };

  const getDaysInState = (doc: DocDocument): number => {
    if (!doc.lastEditedAt && !doc.generatedAt) return 0;
    const dateStr = doc.lastEditedAt || doc.generatedAt;
    if (!dateStr) return 0;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  };

  const getTemplate = (doc: DocDocument): DocTemplate | undefined =>
    templates.find(t => t.id === doc.templateId);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '--';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  };

  const downloadMarkdown = (doc: DocDocument) => {
    const blob = new Blob([doc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadHTML = (doc: DocDocument) => {
    // Simple markdown-to-HTML conversion for export
    let body = doc.content
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // Headings
      .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold and italic
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Horizontal rules
      .replace(/^---$/gm, '<hr>')
      // Line breaks (double newline = paragraph)
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    body = '<p>' + body + '</p>';
    // Clean up empty paragraphs
    body = body.replace(/<p><\/p>/g, '').replace(/<p>(<h[1-4]>)/g, '$1').replace(/(<\/h[1-4]>)<\/p>/g, '$1');

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${doc.title}</title>
<style>
  body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #18181B; line-height: 1.8; font-size: 14px; }
  h1 { font-size: 20px; font-weight: 700; border-bottom: 2px solid #18181B; padding-bottom: 6px; margin: 28px 0 16px; }
  h2 { font-size: 16px; font-weight: 700; margin-top: 24px; border-bottom: 1px solid #E4E4E7; padding-bottom: 4px; }
  h3 { font-size: 14px; font-weight: 700; margin-top: 20px; color: #27272A; }
  h4 { font-size: 13px; font-weight: 700; margin-top: 16px; color: #52525B; text-transform: uppercase; letter-spacing: 0.3px; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  th, td { border: 1px solid #E4E4E7; padding: 6px 10px; text-align: left; font-size: 13px; }
  th { background: #F8FAFC; font-weight: 600; font-size: 12px; text-transform: uppercase; }
  blockquote { border-left: 3px solid #E4E4E7; margin: 12px 0; padding: 8px 16px; color: #52525B; }
  hr { border: none; border-top: 1px solid #E4E4E7; margin: 20px 0; }
  @media print { body { margin: 20px; } }
</style>
</head><body>
${body}
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveContent = async () => {
    if (!activeBrandId || !doc) return;
    const updated: DocDocument = {
      ...doc,
      content: editContent,
      version: doc.version + 1,
      lastEditedAt: new Date().toISOString(),
    };
    if (updated.status === 'not_started') updated.status = 'draft';
    setContentSaving(true);
    try {
      await saveDocument(activeBrandId, updated);
      onDocumentUpdated(updated);
      setEditingContent(false);
    } catch (err) {
      console.error('Failed to save content:', err);
    } finally {
      setContentSaving(false);
    }
  };

  const handleSavePlaceholder = async (varName: string, value: string) => {
    if (!activeBrandId) return;
    const placeholder = `{{${varName}}}`;
    // Replace across ALL documents that contain this variable
    for (const d of documents) {
      if (!d.content || !d.content.includes(placeholder)) continue;
      const newContent = d.content.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), value);
      const updated: DocDocument = {
        ...d,
        content: newContent,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: 'placeholder-fill',
      };
      await saveDocument(activeBrandId, updated);
      onDocumentUpdated(updated);
    }
  };

  // ── RENDER ──

  const doc = documents.find(d => d.id === selectedDocId);
  if (!doc) {
    return (
      <div>
        <button className="dc-back" onClick={handleGoBack}>{'\u2190'} Back to Pipeline</button>
        <div className="dc-empty">Document not found.</div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[doc.status];
  const tpl = getTemplate(doc);
  const depInfo = getDepInfo(doc);
  const days = getDaysInState(doc);

  return (
    <>
      <button className="dc-back" onClick={handleGoBack}>{'\u2190'} {reviewActive ? 'Exit Review' : 'Back to Pipeline'}</button>

      <div className={reviewActive ? 'dc-review-layout' : ''}>
      <div className={`dc-detail ${reviewActive ? 'dc-review-doc' : ''}`} style={{ background: C.bg, color: C.text }}>

        {/* ── Compact header during review (score + title only) ── */}
        {reviewActive ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            {doc.auditScore != null && (
              <span className="dc-badge" style={{
                color: doc.auditScore >= 95 ? C.success : doc.auditScore >= 80 ? C.amber : C.red,
                background: doc.auditScore >= 95 ? C.successBg : doc.auditScore >= 80 ? C.amberBg : C.redBg,
                fontSize: 13, padding: '4px 12px', fontWeight: 700,
              }}>
                {doc.auditScore}%
              </span>
            )}
            <h2 className="dc-detail-title" style={{ marginBottom: 0, fontSize: 16 }}>{doc.title}</h2>
          </div>
        ) : (
          <>
            <h2 className="dc-detail-title">{doc.title}</h2>

            {/* Status + badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <span className="dc-badge" style={{ color: sc.color, background: sc.bg, fontSize: 12, padding: '4px 12px' }}>
                {sc.label}
              </span>
              <select
                className="dc-select"
                value={doc.status}
                onChange={(e) => updateDocStatus(doc.id, e.target.value as DocStatus)}
                disabled={saving}
                style={{ maxWidth: 180 }}
              >
                {ALL_STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
              {doc.launchBlocker && (
                <span className="dc-badge" style={{ color: C.red, background: C.redBg, fontSize: 11, padding: '3px 10px' }}>
                  Launch Blocker
                </span>
              )}
              {doc.attorneyReviewRequired && (
                <span className="dc-badge" style={{ color: C.orange, background: C.orangeBg, fontSize: 11, padding: '3px 10px' }}>
                  Attorney Required
                </span>
              )}
              {days > 0 && doc.status !== 'not_started' && (
                <span style={{ fontSize: 12, color: days >= 14 ? C.red : days >= 7 ? C.amber : C.textMuted }}>
                  {days}d in state
                </span>
              )}
            </div>

            {/* Dependency info */}
            {(depInfo.blocks > 0 || depInfo.blockedBy > 0) && (
              <div style={{
                display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: C.textSec,
                background: C.bgSoft, borderRadius: 3, padding: '8px 12px', border: `1px solid ${C.border}`,
              }}>
                {depInfo.blocks > 0 && (
                  <span>Blocks <strong style={{ color: C.orange }}>{depInfo.blocks}</strong> document(s)</span>
                )}
                {depInfo.blockedBy > 0 && (
                  <span>Depends on <strong style={{ color: C.blue }}>{depInfo.blockedBy}</strong> document(s)</span>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Hidden during review: audit, metadata, template info ── */}
        {!reviewActive && doc.auditReport && (() => {
          const rpt = doc.auditReport;
          // v5: Traffic light overlay — GREEN (>=90) / YELLOW (70-89) / RED (<70)
          const trafficLight = rpt.score >= 90 ? 'GREEN' : rpt.score >= 70 ? 'YELLOW' : 'RED';
          const trafficColor = trafficLight === 'GREEN' ? C.success : trafficLight === 'YELLOW' ? C.amber : C.red;
          const trafficBg = trafficLight === 'GREEN' ? C.successBg : trafficLight === 'YELLOW' ? C.amberBg : C.redBg;
          const scoreColor = rpt.passed ? C.success : rpt.score >= 80 ? C.amber : C.red;
          const scoreBg = rpt.passed ? C.successBg : rpt.score >= 80 ? C.amberBg : C.redBg;
          const criticalSections = rpt.sections.filter(s => s.weight === 'critical');
          const highSections = rpt.sections.filter(s => s.weight === 'high');
          const criticalPassed = criticalSections.filter(s => s.found).length;
          const highPassed = highSections.filter(s => s.found).length;

          // v5: Fill stage display
          const fillStageLabels: Record<string, string> = {
            skeleton: 'Skeleton', domain_context: 'Domain Context', variables_resolved: 'Vars Resolved',
            cross_doc_verified: 'Cross-Doc OK', gate_passed: 'Gate Passed',
            attorney_reviewed: 'Atty Reviewed', signed: 'Signed',
          };

          return (
            <div style={{
              background: C.bgSoft, border: `1px solid ${C.border}`,
              borderRadius: 4, marginBottom: 16, overflow: 'hidden',
            }}>
              <div style={{
                padding: '16px 16px 12px', borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                  border: `3px solid ${scoreColor}`, background: scoreBg,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{rpt.score}</div>
                  <div style={{ fontSize: 9, color: scoreColor, fontWeight: 600 }}>/ 100</div>
                </div>

                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: scoreColor }}>
                      Quality Gate: {rpt.passed ? 'PASSED' : 'NEEDS WORK'}
                    </div>
                    {/* v5: Traffic light badge */}
                    <span className="dc-badge" style={{
                      color: trafficColor, background: trafficBg,
                      fontSize: 10, padding: '2px 8px', fontWeight: 700, letterSpacing: '0.5px',
                    }}>
                      {trafficLight}
                    </span>
                    {/* v5: Fill stage badge */}
                    {rpt.fillStage && (
                      <span className="dc-badge" style={{
                        color: C.blue, background: C.blueBg,
                        fontSize: 10, padding: '2px 8px', fontWeight: 600,
                      }}>
                        {fillStageLabels[rpt.fillStage] || rpt.fillStage}
                        {rpt.fillStageScore != null ? ` (${rpt.fillStageScore}%)` : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>
                    Attempt {rpt.attempt} of {rpt.maxAttempts} | {new Date(rpt.timestamp).toLocaleString()}
                    {rpt.preSanitizeScore != null && (
                      <> | Raw AI: {rpt.preSanitizeScore}%</>
                    )}
                  </div>
                  <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${rpt.score}%`, borderRadius: 3, background: scoreColor, transition: 'width 0.5s ease' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{criticalPassed}/{criticalSections.length}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600 }}>CRITICAL</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{highPassed}/{highSections.length}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600 }}>HIGH</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{rpt.sections.filter(s => s.found).length}/{rpt.sections.length}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600 }}>TOTAL</div>
                  </div>
                </div>
              </div>

              {/* v5: Plain-English summary for Kevin/Collin */}
              {rpt.summary && (
                <div style={{
                  padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
                  fontSize: 13, color: C.textSec, lineHeight: 1.5,
                  background: C.bg,
                }}>
                  {rpt.summary}
                </div>
              )}

              {(rpt.missingCritical.length > 0 || rpt.missingHigh.length > 0) && (
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, background: rpt.missingCritical.length > 0 ? C.redBg : C.amberBg }}>
                  {rpt.missingCritical.length > 0 && (
                    <div style={{ fontSize: 12, color: C.red, fontWeight: 600, marginBottom: rpt.missingHigh.length > 0 ? 4 : 0 }}>
                      {'\u2717'} Missing Critical: {rpt.missingCritical.join(' | ')}
                    </div>
                  )}
                  {rpt.missingHigh.length > 0 && (
                    <div style={{ fontSize: 12, color: C.amber, fontWeight: 600 }}>
                      {'\u26A0'} Missing High: {rpt.missingHigh.join(' | ')}
                    </div>
                  )}
                </div>
              )}

              {/* v5: Entity reference issues */}
              {rpt.entityIssues && rpt.entityIssues.length > 0 && (
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, background: C.amberBg }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    Entity Reference Issues
                  </div>
                  {rpt.entityIssues.map((issue, i) => (
                    <div key={i} style={{ fontSize: 12, color: C.amber, marginBottom: 2 }}>
                      {'\u26A0'} {issue}
                    </div>
                  ))}
                </div>
              )}

              {/* v5: Inherited provision coverage */}
              {rpt.provisionCoverage && rpt.provisionCoverage.length > 0 && (
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                    Inherited Provision Coverage
                  </div>
                  {rpt.provisionCoverage.map(p => {
                    const provColor = p.score >= 70 ? C.success : p.score >= 40 ? C.amber : C.red;
                    return (
                      <div key={p.provisionId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: provColor, minWidth: 36, textAlign: 'right' }}>
                          {p.score}%
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.provisionName}</div>
                          <div style={{ fontSize: 11, color: C.textMuted }}>
                            {p.keywordsFound.length}/{p.keywordsRequired.length} keywords
                            {!p.contentDepthPassed && p.keywordsFound.length > 0 && (
                              <span style={{ color: C.amber }}> (shallow — needs more content)</span>
                            )}
                          </div>
                        </div>
                        <div style={{
                          width: 60, height: 4, background: C.border, borderRadius: 2, overflow: 'hidden',
                        }}>
                          <div style={{ height: '100%', width: `${p.score}%`, background: provColor, borderRadius: 2 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Provision breakdown */}
              <div style={{ padding: '12px 16px 4px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Critical Provisions (70% of score)
                </div>
                {criticalSections.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 14, lineHeight: '20px', flexShrink: 0, color: s.found ? C.success : C.red }}>
                      {s.found ? '\u2713' : '\u2717'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: s.found ? C.text : C.red }}>{s.title}</div>
                      {s.matchedKeywords.length > 0 && (
                        <div style={{ fontSize: 11, color: C.success, marginTop: 2 }}>Matched: {s.matchedKeywords.join(', ')}</div>
                      )}
                      {s.missingKeywords.length > 0 && (
                        <div style={{ fontSize: 11, color: s.found ? C.textMuted : C.red, marginTop: 2 }}>
                          {s.found ? 'Also looked for: ' : 'Missing: '}{s.missingKeywords.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ padding: '12px 16px 16px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  High Priority Provisions (30% of score)
                </div>
                {highSections.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 14, lineHeight: '20px', flexShrink: 0, color: s.found ? C.success : C.amber }}>
                      {s.found ? '\u2713' : '\u2717'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: s.found ? C.text : C.amber }}>{s.title}</div>
                      {s.matchedKeywords.length > 0 && (
                        <div style={{ fontSize: 11, color: C.success, marginTop: 2 }}>Matched: {s.matchedKeywords.join(', ')}</div>
                      )}
                      {s.missingKeywords.length > 0 && (
                        <div style={{ fontSize: 11, color: s.found ? C.textMuted : C.amber, marginTop: 2 }}>
                          {s.found ? 'Also looked for: ' : 'Missing: '}{s.missingKeywords.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Audit Score (compact) — only outside review */}
        {!reviewActive && doc.auditScore != null && !doc.auditReport && (
          <div style={{ marginBottom: 12 }}>
            <span className="dc-badge" style={{
              color: doc.auditScore >= 95 ? C.success : doc.auditScore >= 80 ? C.amber : C.red,
              background: doc.auditScore >= 95 ? C.successBg : doc.auditScore >= 80 ? C.amberBg : C.redBg,
              fontSize: 12, padding: '4px 12px',
            }}>
              Audit: {doc.auditScore}%
            </span>
          </div>
        )}

        {/* Metadata, notes, tags, template — all hidden during review */}
        {!reviewActive && (
          <>
            <div className="dc-detail-meta">
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Version</div>
                <div className="dc-detail-field-value">v{doc.version}</div>
              </div>
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Last Edited</div>
                <div className="dc-detail-field-value">{formatDate(doc.lastEditedAt)}</div>
              </div>
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Generated</div>
                <div className="dc-detail-field-value">{formatDate(doc.generatedAt)}</div>
              </div>
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Signed</div>
                <div className="dc-detail-field-value">
                  {doc.signedAt ? `${formatDate(doc.signedAt)}${doc.signedBy ? ` by ${doc.signedBy}` : ''}` : '--'}
                </div>
              </div>
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Expires</div>
                <div className="dc-detail-field-value">{formatDate(doc.expiresAt)}</div>
              </div>
              <div className="dc-detail-field">
                <div className="dc-detail-field-label">Edited By</div>
                <div className="dc-detail-field-value">{doc.lastEditedBy || '--'}</div>
              </div>
            </div>

            {doc.attorneyNotes && (
              <div style={{ marginBottom: 16 }}>
                <div className="dc-detail-field">
                  <div className="dc-detail-field-label">Attorney Notes</div>
                  <div className="dc-detail-field-value" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {doc.attorneyNotes}
                  </div>
                </div>
              </div>
            )}

            {doc.attorneyReviewedAt && (
              <div style={{ marginBottom: 16 }}>
                <div className="dc-detail-field">
                  <div className="dc-detail-field-label">Attorney Reviewed</div>
                  <div className="dc-detail-field-value">{formatDate(doc.attorneyReviewedAt)}</div>
                </div>
              </div>
            )}

            {doc.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {doc.tags.map(tag => (
                  <span key={tag} className="dc-badge" style={{ color: C.textSec, background: C.borderLight, fontSize: 11, padding: '3px 8px' }}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {tpl && (
              <div style={{
                background: C.bgSoft, border: `1px solid ${C.borderLight}`,
                borderRadius: 3, padding: 12, marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  Template
                </div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 4 }}>{tpl.title}</div>
                {tpl.description && (
                  <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4, lineHeight: 1.4 }}>{tpl.description}</div>
                )}
                {tpl.requiredVariables.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: C.textMuted }}>Required Variables: </span>
                    <span style={{ fontSize: 11, color: C.textSec }}>{tpl.requiredVariables.join(', ')}</span>
                  </div>
                )}
                {tpl.estimatedPages > 0 && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                    Est. {tpl.estimatedPages} page{tpl.estimatedPages !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Content Area */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Content
            </div>
            {editingContent && (
              <div style={{ display: 'flex', gap: 2 }}>
                {(['source', 'preview'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setEditTab(tab)}
                    style={{
                      fontSize: 11, padding: '3px 10px', border: `1px solid ${C.border}`,
                      borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                      background: editTab === tab ? C.text : C.bg,
                      color: editTab === tab ? C.bg : C.textSec,
                    }}
                  >
                    {tab === 'source' ? 'Source' : 'Preview'}
                  </button>
                ))}
              </div>
            )}
          </div>
          {editingContent ? (
            editTab === 'source' ? (
              <textarea
                className="dc-textarea"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Enter document content..."
              />
            ) : (
              <div className="dc-rendered-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{editContent || 'Nothing to preview.'}</ReactMarkdown>
              </div>
            )
          ) : generating && doc.status === 'generating' ? (
            <div className="dc-content-area" style={{ textAlign: 'center', padding: '40px 20px', color: C.textSec }}>
              <div style={{
                width: 36, height: 36, border: `3px solid ${C.borderLight}`,
                borderTopColor: C.accent, borderRadius: '50%',
                animation: 'dc-spin 0.8s linear infinite',
                margin: '0 auto 16px',
              }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>Generating Document...</div>
              <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 16, transition: 'opacity 0.3s' }}>
                {GEN_MESSAGES[genMsgIdx]}
              </div>
              {auditProgress && (
                <div style={{ maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: auditProgress.passed ? C.success : auditProgress.score !== undefined ? C.amber : C.textSec }}>
                      {auditProgress.phase === 'generating' && `Attempt ${auditProgress.attempt} of ${auditProgress.maxAttempts} \u2014 Drafting`}
                      {auditProgress.phase === 'scoring' && `Attempt ${auditProgress.attempt} of ${auditProgress.maxAttempts} \u2014 Scoring`}
                      {auditProgress.phase === 'fixing' && `Attempt ${auditProgress.attempt} of ${auditProgress.maxAttempts} \u2014 Fixing (${auditProgress.score}%)`}
                    </div>
                    {auditProgress.score !== undefined && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: auditProgress.passed ? C.success : auditProgress.score >= 80 ? C.amber : C.red }}>
                        {auditProgress.passed ? `${auditProgress.score}% PASS` : `${auditProgress.score}%`}
                      </div>
                    )}
                  </div>
                  <div className="dc-progress-bar" style={{ height: 8 }}>
                    <div className="dc-progress-fill" style={{
                      width: `${Math.round((auditProgress.attempt / auditProgress.maxAttempts) * 100)}%`,
                      background: auditProgress.passed ? C.success : auditProgress.score !== undefined ? C.amber : C.accent,
                    }} />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div ref={docContentRef} className="dc-rendered-content" style={reviewActive ? { maxHeight: 'none' } : undefined}>
              {doc.content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
              ) : (
                <p style={{ color: C.textMuted, fontStyle: 'italic' }}>No content yet. Click Generate Draft or Edit to add content.</p>
              )}
            </div>
          )}
        </div>

        {/* Generation Error — hidden during review */}
        {!reviewActive && genError && (
          <div style={{
            background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 3,
            padding: 12, marginBottom: 12, fontSize: 13, color: C.red,
          }}>
            <strong>Generation Error:</strong> {genError}
            <button onClick={() => clearGenError()}
              style={{ marginLeft: 12, background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontWeight: 600 }}>
              Dismiss
            </button>
          </div>
        )}

        {/* Generation Result Banner — hidden during review */}
        {!reviewActive && genResult && (
          <div style={{
            background: genResult.hasGate ? (genResult.passed ? C.successBg : C.amberBg) : C.blueBg,
            border: `1px solid ${genResult.hasGate ? (genResult.passed ? C.success : C.amber) : C.blue}`,
            borderRadius: 3, padding: 12, marginBottom: 12, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <strong style={{ color: C.text }}>Generation Complete</strong>
              <span style={{ color: C.textSec, marginLeft: 8 }}>
                {genResult.elapsed}s
                {genResult.hasGate && genResult.score != null && (
                  <> | Score: <span style={{ fontWeight: 700, color: genResult.passed ? C.success : (genResult.score >= 80 ? C.amber : C.red) }}>
                    {genResult.score}%
                  </span>
                  {genResult.passed ? ' PASSED' : ' NEEDS WORK'}
                  {genResult.attempts && genResult.attempts > 1 ? ` (${genResult.attempts} attempts)` : ''}
                  </>
                )}
                {!genResult.hasGate && <> | No quality gate on this template</>}
              </span>
            </div>
            <button onClick={() => clearGenResult()}
              style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              {'\u2715'}
            </button>
          </div>
        )}

        {/* Action Buttons — simplified during review */}
        {reviewActive ? (
          <div className="dc-actions" style={{ marginTop: 12 }}>
            <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'attorney_approved')}
              disabled={saving} style={{ borderColor: C.blue, color: C.blue }}>
              Mark Approved
            </button>
          </div>
        ) : (
          <div className="dc-actions">
            {editingContent ? (
              <>
                <button className="dc-btn dc-btn-primary" onClick={handleSaveContent} disabled={contentSaving}>
                  {contentSaving ? 'Saving...' : 'Save Content'}
                </button>
                <button className="dc-btn dc-btn-secondary" onClick={() => setEditingContent(false)} disabled={contentSaving}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                {(doc.status === 'not_started' || doc.status === 'draft') && (
                  <button className="dc-btn dc-btn-primary" onClick={() => generateDocument(doc.id)}
                    disabled={generating || saving} style={{ background: C.accent }}>
                    {generating ? 'Generating...' : doc.content ? 'Regenerate Draft' : 'Generate Draft'}
                  </button>
                )}
                <button className="dc-btn dc-btn-primary" onClick={() => { setEditContent(doc.content || ''); setEditingContent(true); }}>
                  Edit Content
                </button>
                {doc.content && /\{\{[^}]+\}\}/.test(doc.content) && (() => {
                  const count = new Set((doc.content.match(/\{\{([^}]+)\}\}/g) || []).map(m => m.slice(2, -2))).size;
                  return (
                    <button
                      className="dc-btn dc-btn-primary"
                      onClick={() => setShowPlaceholderModal(true)}
                      style={{ background: C.orange }}
                    >
                      Fill Variables ({count})
                    </button>
                  );
                })()}
                <button className="dc-btn dc-btn-secondary" onClick={() => copyToClipboard(doc.content || '')} disabled={!doc.content}>
                  Copy
                </button>
                <button className="dc-btn dc-btn-secondary" onClick={() => downloadMarkdown(doc)} disabled={!doc.content}>
                  .md
                </button>
                <button className="dc-btn dc-btn-secondary" onClick={() => downloadHTML(doc)} disabled={!doc.content}>
                  .html
                </button>
                {doc.status !== 'attorney_review' && doc.attorneyReviewRequired && (
                  <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'attorney_review')}
                    disabled={saving || !doc.content} style={{ borderColor: C.orange, color: C.orange }}>
                    Send to Attorney
                  </button>
                )}
                {doc.status === 'attorney_review' && (
                  <>
                    <button
                      className="dc-btn-thomas"
                      onClick={() => {
                        if (!activeBrandConfig || !tpl) return;
                        startReview(doc, tpl, activeBrandConfig);
                      }}
                      disabled={reviewActive || reviewLoading || !activeBrandConfig || !doc.content}
                    >
                      {reviewLoading ? 'Opening...' : reviewActive ? 'Review Active' : 'Review with Thomas'}
                    </button>
                    <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'attorney_approved')}
                      disabled={saving} style={{ borderColor: C.blue, color: C.blue }}>
                      Mark Approved
                    </button>
                  </>
                )}
                {doc.status === 'attorney_approved' && (
                  <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'final')}
                    disabled={saving} style={{ borderColor: C.success, color: C.success }}>
                    Mark Final
                  </button>
                )}
                {doc.status === 'final' && (
                  <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'signed')}
                    disabled={saving} style={{ borderColor: C.success, color: C.success }}>
                    Mark Signed
                  </button>
                )}
                {doc.status === 'draft' && !doc.attorneyReviewRequired && (
                  <button className="dc-btn dc-btn-secondary" onClick={() => updateDocStatus(doc.id, 'final')}
                    disabled={saving} style={{ borderColor: C.success, color: C.success }}>
                    Mark Final
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Thomas Review Panel */}
      {reviewActive && reviewSession && (
        <div className="dc-review-panel">
          <ReviewPanel
            session={reviewSession}
            isLoading={reviewLoading}
            isGenerating={reviewGenerating}
            error={reviewError}
            onSendMessage={reviewSendMessage}
            onSkipIssue={reviewSkipIssue}
            onEndReview={reviewEndReview}
            onGenerateFinal={reviewGenerateFinal}
            onRestartReview={() => {
              if (!activeBrandConfig || !tpl) return;
              restartReview(doc, tpl, activeBrandConfig);
            }}
          />
        </div>
      )}
      </div>

      {/* Placeholder Fill Modal */}
      <PlaceholderModal
        content={doc.content || ''}
        isOpen={showPlaceholderModal}
        onClose={() => setShowPlaceholderModal(false)}
        onSaveValue={handleSavePlaceholder}
      />
    </>
  );
};
